/**
 * Dev-only profiling harness for the image viewer.
 *
 * Establishes a reproducible performance baseline for the annotation-heavy image page so that
 * remediation work can be measured rather than assumed.  See
 * IMAGE_VIEWER_PERFORMANCE_INVESTIGATION.md.
 *
 * Disabled unless BOTH:
 *   - the build is not a production build, and
 *   - the URL contains ?profile=1 (or localStorage "lucidityProfile" === "1").
 *
 * When disabled every entry point is an immediate return and <ViewerProfiler> renders its children
 * untouched, so there is no measurable cost and no behavioural difference.
 *
 * Usage:
 *   1. Open an image with ?profile=1 appended to the URL.
 *   2. In the browser console: __lucidityProfile.reset()
 *   3. Perform ONE gesture (a pan, a zoom, a sidebar hover, drop one annotation).
 *   4. __lucidityProfile.report()
 *
 * Report each gesture separately — mixing them makes the per-action cost unreadable.
 *
 * Caveats:
 *   - transferSize/encodedBodySize are reported as 0 for cross-origin requests that lack a
 *     Timing-Allow-Origin header, so payload sizes and the compression flag are only trustworthy
 *     when the API is same-origin (e.g. via the dev proxy).
 *   - performance.memory is Chromium-only and may be unavailable without cross-origin isolation.
 *
 * Data handling: only DOM node counts, action type strings, heap size and resource-timing
 * metadata are collected. No annotation content, patient identifiers or sponsor data is read, and
 * output goes to the browser console only, never to a server or log sink.
 */
/* eslint-disable no-console */
import React, { Profiler, ProfilerOnRenderCallback, ReactNode } from "react";
import { Middleware } from "@reduxjs/toolkit";

type Stat = {
  readonly count: number;
  readonly totalMs: number;
  readonly maxMs: number;
};

const EMPTY_STAT: Stat = { count: 0, totalMs: 0, maxMs: 0 };

const actionCounts = new Map<string, number>();
const commitStats = new Map<string, Stat>();
const timingStats = new Map<string, Stat>();
const marks = new Map<string, number>();

const enabled: boolean =
  process.env.NODE_ENV !== "production" &&
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).get("profile") === "1" ||
    window.localStorage.getItem("lucidityProfile") === "1");

const now = (): number => (typeof performance === "undefined" ? 0 : performance.now());

const round = (n: number): number => Math.round(n * 100) / 100;

const addSample = (into: Map<string, Stat>, key: string, ms: number): void => {
  const prev = into.get(key) || EMPTY_STAT;
  into.set(key, {
    count: prev.count + 1,
    totalMs: prev.totalMs + ms,
    maxMs: Math.max(prev.maxMs, ms)
  });
};

const statRows = (from: Map<string, Stat>) =>
  Object.fromEntries(
    Array.from(from.entries())
      .sort((a, b) => b[1].totalMs - a[1].totalMs)
      .map(([key, s]) => [
        key,
        {
          count: s.count,
          totalMs: round(s.totalMs),
          avgMs: round(s.totalMs / s.count),
          maxMs: round(s.maxMs)
        }
      ])
  );

/** Count of live DOM nodes, broken down by the things the annotation layer creates. */
const domCounts = () => ({
  totalNodes: document.getElementsByTagName("*").length,
  svgPaths: document.querySelectorAll(".leaflet-overlay-pane path").length,
  canvases: document.querySelectorAll(".leaflet-overlay-pane canvas").length,
  markers: document.querySelectorAll(".leaflet-marker-icon").length,
  popups: document.querySelectorAll(".leaflet-popup").length,
  tooltips: document.querySelectorAll(".leaflet-tooltip").length
});

/** usedJSHeapSize is Chromium-only; undefined elsewhere. */
const heapMb = (): number | "unavailable" => {
  const mem = (performance as any).memory;
  return mem ? round(mem.usedJSHeapSize / 1024 / 1024) : "unavailable";
};

/**
 * Payload and transfer stats for the annotation fetch, from the Resource Timing API.
 * encodedBodySize < decodedBodySize indicates compression is active on the response.
 */
const imageRequests = () =>
  (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function"
    ? []
    : performance.getEntriesByType("resource")
  )
    .filter(e => /\/api\/images\/[0-9a-f-]{36}(\?|$)/i.test(e.name))
    .map(e => {
      const r = e as PerformanceResourceTiming;
      return {
        url: r.name.replace(/^https?:\/\/[^/]+/, ""),
        durationMs: round(r.duration),
        transferKb: round(r.transferSize / 1024),
        encodedKb: round(r.encodedBodySize / 1024),
        decodedKb: round(r.decodedBodySize / 1024),
        compressed:
          r.encodedBodySize > 0 && r.decodedBodySize > r.encodedBodySize
            ? `yes (${round(r.decodedBodySize / r.encodedBodySize)}x)`
            : "NO"
      };
    });

export function recordAction(type: string): void {
  if (!enabled) return;
  actionCounts.set(type, (actionCounts.get(type) || 0) + 1);
}

/** Wrap an expensive derived computation to count how often it actually runs. */
export function timed<T>(label: string, compute: () => T): T {
  if (!enabled) return compute();
  const start = now();
  const result = compute();
  addSample(timingStats, label, now() - start);
  return result;
}

/** Redux middleware counting every dispatched action. Inert unless profiling is enabled. */
export const profilingMiddleware: Middleware = () => next => (action: any) => {
  recordAction(action && action.type ? String(action.type) : "(untyped)");
  return next(action);
};

const onRender: ProfilerOnRenderCallback = (id, phase, actualDuration) => {
  addSample(commitStats, `${id} (${phase})`, actualDuration);
};

/** Wraps a subtree in a React Profiler when profiling is on; otherwise renders children as-is. */
export const ViewerProfiler = ({ id, children }: { id: string; children: ReactNode }) => {
  if (!enabled) return <>{children}</>;
  return (
    <Profiler id={id} onRender={onRender}>
      {children}
    </Profiler>
  );
};

export function reset(): void {
  actionCounts.clear();
  commitStats.clear();
  timingStats.clear();
  marks.clear();
  marks.set("startedAt", now());
  console.log("[lucidity-profile] counters reset — perform ONE gesture, then report()");
}

export function report(): void {
  const started = marks.get("startedAt");
  const elapsed = started === undefined ? undefined : round(now() - started);
  const actions = Array.from(actionCounts.entries()).sort((a, b) => b[1] - a[1]);
  const totalActions = actions.reduce((sum, [, n]) => sum + n, 0);
  const commits = Array.from(commitStats.values()).reduce((sum, s) => sum + s.count, 0);
  const renderMs = round(Array.from(commitStats.values()).reduce((sum, s) => sum + s.totalMs, 0));

  console.group(
    `[lucidity-profile] ${totalActions} actions · ${commits} commits · ${renderMs}ms render` +
      (elapsed === undefined ? " (no reset() called)" : ` over ${elapsed}ms`)
  );
  console.log("React commits (actualDuration, ms):");
  console.table(statRows(commitStats));
  console.log("Instrumented computations (ms):");
  console.table(statRows(timingStats));
  console.log("Redux actions dispatched:");
  console.table(Object.fromEntries(actions));
  console.log("DOM:");
  console.table(domCounts());
  console.log("Annotation fetches:");
  console.table(imageRequests());
  console.log(`JS heap (MB): ${heapMb()}`);
  console.groupEnd();
}

if (enabled && typeof window !== "undefined") {
  // Exposing a console-callable global is the whole point of this module; there is no
  // non-mutating way to attach one.
  // configurable/writable so that a hot-reload re-executing this module redefines the global
  // rather than throwing on a non-configurable property.
  // eslint-disable-next-line functional/immutable-data
  Object.defineProperty(window, "__lucidityProfile", {
    value: { reset, report },
    configurable: true,
    writable: true
  });
  reset();
  console.log("[lucidity-profile] enabled — use __lucidityProfile.reset() / .report()");
}
